import type { Table } from 'primeng/table';

// PrimeNG's column resize works in screen pixels, but the app applies its own zoom (`--systemZoom` on
// <html>), so the drag line and the new width drift from the pointer unless both are scaled back. Used
// by the Home List and Tasks tables.
//
// It also starts the drag line exactly at the pressed point. PrimeNG only places the line on a
// mouse press — on a touch press it keeps the place of the previous resize, so on a tablet a tap on
// the handle without a drag (or a drag whose first move is lost) resized the column by a random amount.

const patchedTables = new WeakSet<Table>();

export function activeSystemZoom(): number {
  if (typeof window === 'undefined') return 1;
  const zoom = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--systemZoom').trim());
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

/** Makes a p-table's column resizing zoom-aware. Safe to call repeatedly for the same table. */
export function patchTableColumnResizeForZoom(table?: Table): void {
  if (!table || patchedTables.has(table)) return;

  const originalBegin = table.onColumnResizeBegin?.bind(table);
  const originalMove = table.onColumnResize?.bind(table);
  if (!originalBegin || !originalMove) return;

  table.onColumnResizeBegin = (event: Event) => {
    originalBegin(event);
    const zoom = activeSystemZoom();
    if (Math.abs(zoom - 1) >= 0.001 && typeof table.lastResizerHelperX === 'number') {
      table.lastResizerHelperX = table.lastResizerHelperX / zoom;
    }
    const helperEl = table.resizeHelperViewChild?.nativeElement as HTMLElement | undefined;
    if (helperEl && typeof table.lastResizerHelperX === 'number' && Number.isFinite(table.lastResizerHelperX)) {
      helperEl.style.left = `${table.lastResizerHelperX}px`;
    }
  };

  table.onColumnResize = (event: Event) => {
    originalMove(event);
    const zoom = activeSystemZoom();
    if (Math.abs(zoom - 1) < 0.001) return;
    const helperEl = table.resizeHelperViewChild?.nativeElement as HTMLElement | undefined;
    const currentLeft = Number.parseFloat(helperEl?.style.left || '');
    if (helperEl && Number.isFinite(currentLeft)) helperEl.style.left = `${currentLeft / zoom}px`;
  };

  enableColumnAutoFit(table);
  patchedTables.add(table);
}

// Double-click (double-tap on a touch screen) on a column's resize handle fits the column on its left to
// its contents: as wide as the widest cell on the page needs without wrapping, the header included.
// A touch press on the handle is preventDefault-ed by PrimeNG, so no dblclick comes from a finger —
// two taps in quick succession on the same handle are detected here instead.

const DOUBLE_TAP_MS = 400;
const RESIZER_SELECTOR = '.p-datatable-column-resizer';

interface ResizableTableInternals {
  el?: { nativeElement: HTMLElement };
  tableViewChild?: { nativeElement: HTMLTableElement };
  resizeColumnElement?: HTMLElement;
  _initialColWidths?: number[];
  _totalTableWidth?: () => number[];
  setResizeTableWidth?: (width: string) => void;
  resizeTableCells?: (newColumnWidth: number, nextColumnWidth: number | null) => void;
  onColResize?: { emit: (event: { element: HTMLElement; delta: number }) => void };
  isStateful?: () => boolean;
  saveState?: () => void;
}

/** Width of an element's contents laid out on one line, measured on a hidden copy inside the same cell
 * (so it gets the cell's font and styles). The column's resize handle is left out. */
function singleLineContentWidth(cell: HTMLElement): number {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;left:0;top:0;visibility:hidden;pointer-events:none;white-space:nowrap;width:max-content;max-width:none;';
  cell.childNodes.forEach((child) => {
    if (child instanceof HTMLElement && child.matches(RESIZER_SELECTOR)) return;
    probe.appendChild(child.cloneNode(true));
  });
  probe.querySelectorAll<HTMLElement>('*').forEach((el) => (el.style.whiteSpace = 'nowrap'));
  cell.appendChild(probe);
  const width = probe.getBoundingClientRect().width / activeSystemZoom();
  probe.remove();
  return width;
}

/** The width a column cell needs for its contents on one line: content + padding + borders. */
export function fittedCellWidth(cell: HTMLElement): number {
  const style = getComputedStyle(cell);
  const extra = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth']
    .map((prop) => Number.parseFloat(style[prop as keyof CSSStyleDeclaration] as string) || 0)
    .reduce((a, b) => a + b, 0);
  return Math.ceil(singleLineContentWidth(cell) + extra) + 1;
}

/** Fits the column of header cell `th` to the widest of its header and body cells on the current page. */
export function autoFitColumn(table: Table, th: HTMLElement): void {
  const t = table as unknown as ResizableTableInternals;
  const tableEl = t.tableViewChild?.nativeElement;
  const row = th.parentElement;
  if (!tableEl || !row || !t._totalTableWidth || !t.setResizeTableWidth || !t.resizeTableCells) return;

  const index = Array.from(row.children).indexOf(th);
  if (index < 0) return;
  const cells: HTMLElement[] = [th];
  tableEl.querySelectorAll<HTMLTableRowElement>(':scope > tbody > tr').forEach((tr) => {
    const td = tr.children[index] as HTMLTableCellElement | undefined;
    if (td && td.colSpan === 1) cells.push(td);
  });

  const minWidth = Number.parseFloat(th.style.minWidth.replace(/[^\d.]/g, '')) || 15;
  const newWidth = Math.max(minWidth, ...cells.map(fittedCellWidth));
  const delta = newWidth - th.offsetWidth;
  if (Math.abs(delta) < 1) return;

  // The same steps as PrimeNG's own end of a drag in 'expand' mode (onColumnResizeEnd).
  t._initialColWidths = t._totalTableWidth();
  t.setResizeTableWidth(`${tableEl.offsetWidth + delta}px`);
  t.resizeColumnElement = th;
  t.resizeTableCells(newWidth, null);
  t.onColResize?.emit({ element: th, delta });
  if (t.isStateful?.()) t.saveState?.();
}

function enableColumnAutoFit(table: Table): void {
  const host = (table as unknown as ResizableTableInternals).el?.nativeElement;
  if (!host) return;

  const resizerHeader = (event: Event): HTMLElement | null => {
    const resizer = (event.target as Element | null)?.closest?.(RESIZER_SELECTOR);
    return resizer && host.contains(resizer) ? (resizer.closest('th') as HTMLElement | null) : null;
  };

  // PrimeNG shows its resize line under the pointer while the button is down, so the release lands on
  // that line and the browser sends the dblclick to the table rather than to the handle: the handle
  // pressed last is remembered instead.
  let lastPress: { th: HTMLElement; time: number } | null = null;
  host.addEventListener(
    'mousedown',
    (event) => {
      const th = resizerHeader(event);
      lastPress = th ? { th, time: Date.now() } : null;
    },
    true,
  );

  // Capture phase: the header's own (dbl)click handlers — sorting — never see it.
  host.addEventListener(
    'dblclick',
    (event) => {
      const th = resizerHeader(event) ?? (lastPress && Date.now() - lastPress.time <= 1000 ? lastPress.th : null);
      lastPress = null;
      if (!th) return;
      event.stopPropagation();
      event.preventDefault();
      // After PrimeNG has finished the (zero-width) drag of the second click.
      setTimeout(() => autoFitColumn(table, th));
    },
    true,
  );

  let lastTap: { th: HTMLElement; time: number; x: number } | null = null;
  host.addEventListener(
    'touchend',
    (event) => {
      const th = resizerHeader(event);
      const touch = event.changedTouches[0];
      if (!th || !touch) {
        lastTap = null;
        return;
      }
      const now = Date.now();
      // A drag (the handle moved away from where the first tap was) is not a tap.
      if (lastTap && lastTap.th === th && now - lastTap.time <= DOUBLE_TAP_MS && Math.abs(touch.clientX - lastTap.x) < 12) {
        lastTap = null;
        setTimeout(() => autoFitColumn(table, th));
        return;
      }
      lastTap = { th, time: now, x: touch.clientX };
    },
    true,
  );
}
